import type { Control, FieldValues } from 'react-hook-form';
import type { OdinDataRetrieval } from '../../../common/data-retrieval/data-retrieval-interfaces';
import OdinController from '../../dynamic-form/odin-controller.component';
import { useResolveDynamicFormData } from '../../dynamic-form/use-resolve-dynamic-form-data';
import type { JsonData } from '../../generic-types';
import type { RadioGroupProps } from './radio-group.component';
import RadioGroup from './radio-group.component';

export interface HfRadioGroupProps<TFieldValues extends FieldValues = FieldValues>
  extends Omit<RadioGroupProps, 'data'> {
  control: Control<TFieldValues>;
  name: string;
  /**
   * Static array of options, OR an `OdinDataRetrieval` for fields whose data is injected by
   * `DynamicForm`'s `dataRetrieval` prop. Resolved to a static array before being passed to the
   * underlying `RadioGroup`.
   */
  data?: JsonData | OdinDataRetrieval;
}

export default function HfRadioGroup<TFieldValues extends FieldValues = FieldValues>({
  id,
  control,
  name,
  label,
  data,
  value,
  textField,
  valueField,
  readOnly,
  required,
  orientation = 'VERTICAL',
  onChange,
  fieldValueConverter,
  disabled,
  labelStyles,
  archivedField,
  showSelectedArchivedOptions,
}: HfRadioGroupProps<TFieldValues>) {
  const resolvedData = useResolveDynamicFormData(data, id, 'OPTIONGROUP');

  return (
    <OdinController
      name={name}
      control={control}
      onChange={onChange}
      defaultValue={value}
      fieldValueConverter={fieldValueConverter}
      render={({ field: { onChange, value }, fieldState: { error } }) => (
        <RadioGroup
          id={id}
          name={name}
          label={label}
          value={value}
          error={error?.message}
          onChange={onChange}
          textField={textField}
          valueField={valueField}
          data={resolvedData}
          readOnly={readOnly}
          required={required}
          orientation={orientation}
          disabled={disabled}
          labelStyles={labelStyles}
          archivedField={archivedField}
          showSelectedArchivedOptions={showSelectedArchivedOptions}
        />
      )}
    />
  );
}
